/**
 * Builds the printed pick sheet as a self-contained HTML document.
 *
 * Kept pure and separate from the iframe driver so it can be unit tested in
 * vitest's node environment — a print routine that only exists inside a click
 * handler is a print routine nobody ever tests.
 *
 * Everything is inlined: the document is written into a blank iframe, so it has
 * no access to the app's stylesheets, Tailwind, or fonts.
 */

import { code128Svg, sanitizeForCode128 } from './code128'
import {
  CATEGORY_META,
  formatQty,
  formatSheetDate,
  formatSheetDateTime,
  groupByZone,
  lineCountLabel,
  lineLocationLabel,
  lotExpiryStatus,
  pickCategory,
} from './pickList'
import type { PickList, PickListLine } from '@/types/pickList'

/** Print palette. Flat hex because the sheet cannot use Tailwind tokens. */
const INK = {
  navy: '#14375e',
  navySoft: '#1d4b7c',
  zoneBg: '#eef2f7',
  border: '#cbd5e1',
  borderSoft: '#e2e8f0',
  text: '#0f172a',
  muted: '#64748b',
  amber: '#b45309',
  amberBg: '#fffbeb',
  red: '#b91c1c',
  penBox: '#94a3b8',
  link: '#2563eb',
}

export interface PickSheetOptions {
  /** Injected clock so tests are deterministic. */
  now?: number
  /** Printed under the barcode; defaults to the list code. */
  documentTitle?: string
}

export function buildPickSheetHtml(pickList: PickList, opts: PickSheetOptions = {}): string {
  const now = opts.now ?? Date.now()
  const zones = pickList.zones?.length ? pickList.zones : groupByZone(pickList.lines)
  const title = opts.documentTitle ?? `${pickList.code} rev ${pickList.revision}`

  return [
    `<!doctype html><html><head><meta charset="utf-8"/>`,
    `<title>${esc(title)}</title>`,
    `<style>${styles()}</style>`,
    `</head><body>`,
    headerBand(pickList),
    metaGrid(pickList, now),
    table(zones, now),
    footer(pickList, now),
    `</body></html>`,
  ].join('')
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function headerBand(pickList: PickList): string {
  const label = `${pickList.code} rev ${pickList.revision}`
  const encodable = sanitizeForCode128(pickList.code)
  const barcode = code128Svg(encodable, { moduleWidth: 1.35, height: 34, title: label })

  return `
<header class="band">
  <div class="band-main">
    <div class="brand"><span class="brand-mark">SMART-CGT</span><span class="brand-title">INVENTORY PICK LIST</span></div>
    <p class="band-sub">Reagents, consumables and kits &middot; picked in zone route order, FEFO lots suggested by the system</p>
  </div>
  <div class="band-code">
    ${barcode || ''}
    <div class="band-code-label">${esc(label)}</div>
  </div>
</header>`
}

function metaGrid(pickList: PickList, now: number): string {
  const cols: Array<Array<[string, string]>> = [
    [
      ['REQUESTED BY', pickList.requestedBy ?? '—'],
      ['ASSIGNED TO', pickList.assignedToName ?? 'Unassigned'],
      ['CREATED', formatSheetDateTime(pickList.generatedAt)],
      ['REQUIRED BY', formatSheetDateTime(pickList.requiredBy)],
    ],
    [
      ['DESTINATION', pickList.destination ?? '—'],
      ['ORDER REFERENCE', pickList.orderReference ?? '—'],
      ['MANUFACTURING ORDER', pickList.manufacturingOrder ?? '—'],
      ['CHAIN OF IDENTITY', pickList.chainOfIdentity ?? '—'],
    ],
    [
      ['PRIORITY', priorityLabel(pickList.priority)],
      ['STATUS AT PRINT', statusLabel(pickList.status)],
      ['ALLOCATION RULE', allocationLabel(pickList.allocationRule)],
      ['LINE COUNT', pickList.summary?.lineCountLabel ?? lineCountLabel(pickList.lines)],
    ],
  ]
  void now

  return `<section class="meta">${cols
    .map(
      (col) =>
        `<div class="meta-col">${col
          .map(
            ([k, v]) =>
              `<div class="meta-row"><div class="meta-k">${esc(k)}</div><div class="meta-v">${esc(v)}</div></div>`,
          )
          .join('')}</div>`,
    )
    .join('')}</section>`
}

function table(zones: PickList['zones'], now: number): string {
  const head = `
<thead>
  <tr>
    <th class="c-num">#</th>
    <th class="c-cat">CAT</th>
    <th class="c-code">ITEM CODE</th>
    <th class="c-desc">DESCRIPTION</th>
    <th class="c-req">REQ</th>
    <th class="c-lot">SUGGESTED LOT</th>
    <th class="c-exp">EXPIRY</th>
    <th class="c-loc">LOCATION</th>
    <th class="c-pen">QTY PICKED</th>
    <th class="c-pen">LOT PICKED</th>
    <th class="c-ok">OK</th>
  </tr>
</thead>`

  const body = zones.map((z) => zoneBlock(z, now)).join('')
  const empty = zones.length
    ? ''
    : `<tbody><tr><td class="empty" colspan="11">No inventory is required for this selection.</td></tr></tbody>`

  return `<table class="sheet">${head}${body}${empty}</table>`
}

function zoneBlock(zone: PickList['zones'][number], now: number): string {
  const count = `${zone.lines.length} line${zone.lines.length === 1 ? '' : 's'}`
  const temp = zone.tempRange
    ? `<span class="zone-temp">${esc(zone.tempRange)}</span>`
    : ''
  const note = zone.handlingNote
    ? `<span class="zone-note">${esc(zone.handlingNote)}</span>`
    : ''

  return `
<tbody class="zone">
  <tr class="zone-header">
    <td colspan="8">
      <span class="zone-code">${esc(zone.zoneCode)}</span>
      <span class="zone-name">${esc(zone.zoneName)}</span>
      ${temp}${note}
    </td>
    <td class="zone-count" colspan="3">${esc(count)}</td>
  </tr>
  ${zone.lines.map((l) => row(l, now)).join('')}
</tbody>`
}

/**
 * The LOCATION cell, with a break opportunity after each path separator.
 *
 * `lineLocationLabel` returns a `locationCode` or, since the storage hierarchy
 * landed, a materialised `storageNodePath` like `LN2-01/Vapor/R1/C1/B1`. That is
 * an unbroken token roughly twice the width of its column, and the column is
 * fixed — so without a break opportunity it painted straight over QTY PICKED and
 * LOT PICKED (z8nrz7cnaj).
 *
 * `<wbr>` marks where the browser MAY break, so the path prefers to wrap at its
 * own segment boundaries and stays readable as whole codes. The CSS fallback
 * (`overflow-wrap: anywhere`) only takes over if one segment is itself too wide.
 *
 * Segments are escaped individually; the separator is markup we emit, not text
 * from the record.
 */
function locationCell(line: PickListLine): string {
  const label = lineLocationLabel(line)
  if (!label) return '&mdash;'
  return label
    .split('/')
    .map(esc)
    .join('/<wbr>')
}

/**
 * QTY PICKED, LOT PICKED and OK: what was recorded on the sheet, or empty boxes
 * for the picker to fill by hand.
 *
 * A printed sheet is also the record of a pick in progress, so a line already
 * worked prints its saved quantity (amber when short of the requirement) and
 * the lot it came from, with OK ticked once it is fully picked. A line not yet
 * worked keeps its pen boxes. So does a picked line with no lot recorded: the
 * picker writes it in, rather than the sheet inventing one.
 */
function pickedCells(line: PickListLine): string {
  const pen = '<span class="pen"></span>'
  const recorded = line.qtyPicked != null
  const qty = recorded
    ? `<span class="filled${line.status === 'SHORT' ? ' short' : ''}">${esc(formatQty(line.qtyPicked as number, line.uom))}</span>`
    : pen
  const lot = recorded && line.pickedLotNumber ? `<span class="filled">${esc(line.pickedLotNumber)}</span>` : pen
  const ok = line.status === 'PICKED' ? '<span class="tick ticked">&#10003;</span>' : '<span class="tick"></span>'
  return `<td class="c-pen">${qty}</td>
    <td class="c-pen">${lot}</td>
    <td class="c-ok">${ok}</td>`
}

function row(line: PickListLine, now: number): string {
  const cat = CATEGORY_META[pickCategory(line.materialType)]
  const expiry = lotExpiryStatus(line.suggestedExpiry, now)
  const expiryCls = expiry?.expired
    ? 'exp-bad'
    : expiry?.nearExpiry
      ? 'exp-warn'
      : ''

  // Each row's left edge is keyed to its category, and to amber when the line is
  // flagged — a colour-coded rail down the sheet.
  const accent = line.alerts.some((a) => a.severity !== 'info') ? INK.amber : cat.print.bg
  const main = `
  <tr class="line">
    <td class="c-num" style="box-shadow: inset 3px 0 0 ${accent}">${line.lineNo}</td>
    <td class="c-cat"><span class="pill" style="background:${cat.print.bg};color:${cat.print.fg}">${esc(cat.label)}</span></td>
    <td class="c-code">${esc(line.materialCode)}</td>
    <td class="c-desc">${esc(line.materialName)}</td>
    <td class="c-req">${esc(formatQty(line.qtyRequired, line.uom))}</td>
    <td class="c-lot">${esc(line.suggestedLotNumber ?? '—')}</td>
    <td class="c-exp ${expiryCls}">${esc(formatSheetDate(line.suggestedExpiry))}</td>
    <td class="c-loc">${locationCell(line)}</td>
    ${pickedCells(line)}
  </tr>`

  // Alerts print as their own row beneath the line they belong to, so a picker
  // reads the warning in the same place they read the item.
  const alerts = line.alerts
    .filter((a) => a.severity !== 'info')
    .map(
      (a) =>
        `<tr class="alert ${a.severity === 'error' ? 'alert-error' : 'alert-warn'}"><td colspan="11">${esc(a.message)}</td></tr>`,
    )
    .join('')

  return main + alerts
}

function footer(pickList: PickList, now: number): string {
  return `
<footer class="foot">
  <span>${esc(pickList.code)} rev ${pickList.revision}</span>
  <span>Printed ${esc(formatSheetDateTime(new Date(now).toISOString()))}</span>
  <span>Picked by _______________________&nbsp;&nbsp;Checked by _______________________</span>
</footer>`
}

// ─── Labels ───────────────────────────────────────────────────────────────────

function priorityLabel(p: PickList['priority']): string {
  return { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High' }[p] ?? String(p)
}

function statusLabel(s: PickList['status']): string {
  return (
    {
      DRAFT: 'Draft',
      OPEN: 'Not started',
      IN_PROGRESS: 'In Progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    }[s] ?? String(s)
  )
}

function allocationLabel(rule: string): string {
  return rule === 'FEFO'
    ? 'FEFO (first-expiry-first-out), lot-committed at release'
    : rule
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function styles(): string {
  return `
@page { size: A4 portrait; margin: 12mm; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body {
  margin: 0;
  color: ${INK.text};
  font: 11px/1.35 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* Header band */
.band {
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  background: ${INK.navy}; color: #fff; padding: 12px 14px; border-radius: 4px;
}
.brand { display: flex; align-items: baseline; gap: 10px; }
.brand-mark { font-size: 15px; font-weight: 700; letter-spacing: 0.10em; }
.brand-title { font-size: 15px; font-weight: 600; letter-spacing: 0.14em; }
.band-sub { margin: 4px 0 0; font-size: 9.5px; color: #c7d7ea; }
.band-code { text-align: right; background: #fff; padding: 5px 7px 3px; border-radius: 3px; }
.band-code svg { display: block; }
.band-code-label {
  margin-top: 2px; font-size: 8.5px; font-weight: 700; letter-spacing: 0.06em; color: ${INK.navy};
}

/* Metadata grid */
.meta {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px 18px;
  border: 1px solid ${INK.border}; border-top: 0; border-radius: 0 0 4px 4px;
  padding: 9px 12px; margin-bottom: 10px;
}
.meta-col { display: flex; flex-direction: column; gap: 5px; }
.meta-k {
  font-size: 7.5px; font-weight: 700; letter-spacing: 0.08em; color: ${INK.muted};
  text-transform: uppercase;
}
.meta-v { font-size: 10px; font-weight: 600; }

/* Table */
table.sheet { width: 100%; border-collapse: collapse; table-layout: fixed; }
/* Repeat the column head on every printed page. */
thead { display: table-header-group; }
thead th {
  background: ${INK.navy}; color: #fff; font-size: 7.5px; font-weight: 700;
  letter-spacing: 0.07em; text-transform: uppercase; text-align: left;
  padding: 5px 5px; border: 1px solid ${INK.navy};
}
tbody.zone { break-inside: auto; }
tr { break-inside: avoid; }
td { padding: 4px 5px; border: 1px solid ${INK.borderSoft}; vertical-align: top; }

/* Zone header — never orphan it at the foot of a page. */
tr.zone-header { break-after: avoid; }
tr.zone-header td {
  background: ${INK.zoneBg}; border-color: ${INK.border}; padding: 5px 6px;
}
.zone-code { font-weight: 700; font-size: 10px; color: ${INK.navy}; margin-right: 5px; }
.zone-name { font-weight: 700; font-size: 10px; letter-spacing: 0.04em; }
.zone-temp { margin-left: 26px; font-size: 9px; font-weight: 700; color: ${INK.link}; }
.zone-note { margin-left: 8px; font-size: 8.5px; font-style: italic; color: ${INK.muted}; }
.zone-count {
  background: ${INK.zoneBg}; border-color: ${INK.border};
  text-align: right; font-size: 8.5px; font-weight: 700; color: ${INK.muted};
  white-space: nowrap;
}

/* Columns — must sum to 100% under table-layout: fixed. */
.c-num  { width: 3.5%; text-align: right; color: ${INK.muted}; font-variant-numeric: tabular-nums; }
.c-cat  { width: 7.5%; }
.c-code { width: 11%;   font-weight: 700; }
.c-desc { width: 23.5%; }
.c-req  { width: 6%;    font-weight: 700; }
.c-lot  { width: 9.5%; }
.c-exp  { width: 11.5%; }
.c-loc  { width: 9.5%; }
/*
 * Machine tokens — item codes, lot numbers and storage paths — carry no spaces,
 * so under table-layout: fixed they have nowhere to wrap and simply paint over
 * the columns to their right (z8nrz7cnaj). overflow-wrap: anywhere gives them a
 * break opportunity at any character; the wbr markers emitted in the location
 * cell mean a path prefers to break at its own slash separators, falling back to
 * mid-segment only when one segment is itself too wide.
 *
 * NOTHING here keeps nowrap. EXPIRY had it on the reasoning that a date cannot
 * grow unboundedly — true, and beside the point: 31-Dec-2026 is eleven
 * characters and never fitted the 9% it was given, so it printed straight over
 * LOCATION. A column is only safe on nowrap if its widest possible value FITS,
 * which is a measurement, not an argument.
 *
 * The widths below were rebalanced for the real values rather than the assumed
 * ones. EXPIRY and LOCATION take what DESCRIPTION can spare, since a material
 * name has spaces and wraps gracefully where a date and a shelf code do not.
 */
.c-code, .c-lot, .c-loc { overflow-wrap: anywhere; }
/* Cells only — the column head stays in the band's own type. */
td.c-loc { color: ${INK.link}; font-family: ui-monospace, Menlo, monospace; font-size: 10px; }
.c-pen  { width: 7%; overflow-wrap: anywhere; }
.c-ok   { width: 4%;   text-align: center; }

.pill {
  display: inline-block; padding: 1px 5px; border-radius: 3px;
  font-size: 7.5px; font-weight: 700; letter-spacing: 0.05em;
}
/* Near/past expiry is called out in colour AND kept legible in mono print
   (bold weight survives a greyscale printer where the hue does not). */
.exp-warn { color: ${INK.amber}; font-weight: 700; }
.exp-bad  { color: ${INK.red}; font-weight: 700; text-decoration: underline; }

/* Boxes the picker fills in by hand. */
.pen {
  display: block; height: 15px; border: 1px solid ${INK.penBox};
  border-radius: 2px; background: #fff;
}
.tick {
  display: inline-block; width: 13px; height: 13px;
  border: 1px solid ${INK.penBox}; border-radius: 2px; background: #fff;
}
/* What was already recorded, printed where the pen box would be. Bold so it
   reads as an entry, not as part of the demand to its left. */
.filled { display: block; min-height: 15px; font-weight: 700; }
.filled.short { color: ${INK.amber}; }
.tick.ticked {
  font-size: 11px; font-weight: 700; line-height: 13px; text-align: center;
}

tr.alert td {
  border-top: 0; padding: 1px 5px 4px 34px;
  font-size: 8.5px; font-style: italic; font-weight: 600;
}
tr.alert-warn td { color: ${INK.amber}; }
tr.alert-error td { color: ${INK.red}; }

td.empty { text-align: center; color: ${INK.muted}; padding: 18px; font-style: italic; }

.foot {
  display: flex; justify-content: space-between; gap: 12px;
  margin-top: 10px; padding-top: 6px; border-top: 1px solid ${INK.border};
  font-size: 8px; color: ${INK.muted};
}
`
}

function esc(v: string): string {
  return String(v).replace(
    /[<>&"']/g,
    (c) =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  )
}
